import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API_BASE_URL } from '../../config';
import './PropertyListing.css';
import icon from '../../assets/Logo - Side.png';
import ListingsAssistant from '../pricing/ListingsAssistant';

// Fix leaflet default marker icons (webpack issue)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom blue marker
const blueIcon = L.divIcon({
    className: '',
    html: `<div class="map-marker"><svg width="28" height="36" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="14" cy="34" rx="6" ry="2" fill="rgba(0,0,0,0.2)"/>
        <path d="M14 0C6.27 0 0 6.27 0 14C0 24.5 14 36 14 36C14 36 28 24.5 28 14C28 6.27 21.73 0 14 0Z" fill="#1457f5"/>
        <circle cx="14" cy="14" r="6" fill="white"/>
    </svg></div>`,
    iconSize: [28, 36],
    iconAnchor: [14, 36],
    popupAnchor: [0, -38],
});

const activeIcon = L.divIcon({
    className: '',
    html: `<div class="map-marker active"><svg width="34" height="44" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="14" cy="34" rx="6" ry="2" fill="rgba(0,0,0,0.3)"/>
        <path d="M14 0C6.27 0 0 6.27 0 14C0 24.5 14 36 14 36C14 36 28 24.5 28 14C28 6.27 21.73 0 14 0Z" fill="#0d47d1"/>
        <circle cx="14" cy="14" r="6" fill="white"/>
    </svg></div>`,
    iconSize: [34, 44],
    iconAnchor: [17, 44],
    popupAnchor: [0, -46],
});

const formatPrice = (price) =>
    price
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(price)
        : null;

// ─── Geocode helper ──────────────────────────────────────────────────────────
const geocodeCache = {};
const geocodeAddress = async (address) => {
    if (!address) return null;
    if (geocodeCache[address]) return geocodeCache[address];
    try {
        const res = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1`,
            { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        if (data && data[0]) {
            const coords = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
            geocodeCache[address] = coords;
            return coords;
        }
    } catch {}
    return null;
};

// ─── Map bounds fitter ───────────────────────────────────────────────────────
const FitBounds = ({ properties }) => {
    const map = useMap();
    useEffect(() => {
        const pts = properties.filter(p => p.latitude && p.longitude)
            .map(p => [p.latitude, p.longitude]);
        if (pts.length > 0) {
            if (pts.length === 1) {
                map.setView(pts[0], 14);
            } else {
                map.fitBounds(pts, { padding: [40, 40] });
            }
        }
    }, [properties, map]);
    return null;
};

// ─── Filter Pill Dropdown ────────────────────────────────────────────────────
const FilterDropdown = ({ label, active, children }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);
    return (
        <div className={`filter-pill-wrapper ${open ? 'open' : ''}`} ref={ref}>
            <button className={`filter-pill ${active ? 'active' : ''}`} onClick={() => setOpen(o => !o)}>
                {label}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {open && (
                <div className="filter-dropdown-panel">
                    {children}
                    <div className="filter-dropdown-footer">
                        <button className="filter-done-btn" onClick={() => setOpen(false)}>Done</button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Cart Drawer ─────────────────────────────────────────────────────────────
const CartDrawer = ({ cart, onRemove, onClear, onClose }) => {
    const navigate = useNavigate();
    const handleApplyAll = () => {
        onClose();
        navigate(`/apply?properties=${cart.map(p => p.id).join(',')}`);
    };
    return (
        <>
            <div className="cart-overlay" onClick={onClose} />
            <div className="cart-drawer">
                <div className="cart-header">
                    <div className="cart-header-left">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                        </svg>
                        <span>Application Cart</span>
                    </div>
                    <div className="cart-header-right">
                        {cart.length > 0 && <button className="cart-clear-btn" onClick={onClear}>Clear all</button>}
                        <button className="cart-close-btn" onClick={onClose}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>
                </div>

                {cart.length === 0 ? (
                    <div className="cart-empty">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                        </svg>
                        <p>Your cart is empty</p>
                        <span>Click <strong>+ Add to Cart</strong> on any property to apply in bulk.</span>
                    </div>
                ) : (
                    <>
                        <div className="cart-items">
                            {cart.map(property => (
                                <div key={property.id} className="cart-item">
                                    <div className="cart-item-image">
                                        <svg viewBox="0 0 80 60" fill="none"><rect width="80" height="60" fill="#dde3f0" />
                                            <path d="M20 42V28l20-14 20 14v14" stroke="#8fa3cc" strokeWidth="2.5" fill="none" strokeLinejoin="round" />
                                            <rect x="33" y="30" width="14" height="12" rx="1" fill="#8fa3cc" />
                                        </svg>
                                    </div>
                                    <div className="cart-item-info">
                                        <div className="cart-item-name">{property.property_name || 'Rental Property'}</div>
                                        <div className="cart-item-address">{property.address || 'N/A'}</div>
                                        <div className="cart-item-price">
                                            {property.minRent ? `${formatPrice(property.minRent)}/mo` : 'Contact for pricing'}
                                        </div>
                                    </div>
                                    <button className="cart-item-remove" onClick={() => onRemove(property.id)}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="cart-footer">
                            <div className="cart-summary">
                                <span>{cart.length} propert{cart.length > 1 ? 'ies' : 'y'} selected</span>
                                <span className="cart-summary-note">One application, all properties</span>
                            </div>
                            <button className="cart-apply-all-btn" onClick={handleApplyAll}>
                                Apply to All {cart.length} Propert{cart.length > 1 ? 'ies' : 'y'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

// ─── Property Card (grouped with units) ─────────────────────────────────────
const PropertyCard = ({ property, viewMode, inCart, onCartToggle, onClick, onApply, isHighlighted }) => {
    const matchedUnits = property.matchedUnits || property.availableUnits || [];
    const hasMultipleUnits = matchedUnits.length > 1;

    const priceDisplay = (() => {
        if (!property.minRent) return 'Contact for pricing';
        if (!hasMultipleUnits || property.minRent === property.maxRent) return `${formatPrice(property.minRent)}/mo`;
        return `${formatPrice(property.minRent)} – ${formatPrice(property.maxRent)}/mo`;
    })();

    return (
        <div className={`pl-card ${viewMode} ${isHighlighted ? 'highlighted' : ''}`} onClick={onClick}>
            <div className="pl-card-image">
                <div className="pl-image-inner">
                    <div className="pl-image-placeholder">
                        <svg viewBox="0 0 80 60" fill="none">
                            <rect width="80" height="60" fill="#dde3f0" />
                            <path d="M20 42V28l20-14 20 14v14" stroke="#8fa3cc" strokeWidth="2.5" fill="none" strokeLinejoin="round" />
                            <rect x="33" y="30" width="14" height="12" rx="1" fill="#8fa3cc" />
                            <rect x="22" y="28" width="10" height="8" rx="1" fill="#b0bfe0" />
                            <rect x="48" y="28" width="10" height="8" rx="1" fill="#b0bfe0" />
                        </svg>
                    </div>
                </div>
                <span className="pl-for-rent-badge">For Rent</span>
                {matchedUnits.length > 0
                    ? <span className="pl-units-badge">{matchedUnits.length} unit{matchedUnits.length > 1 ? 's' : ''} available</span>
                    : <span className="pl-units-badge unavailable">No units listed</span>
                }
                <button
                    className={`pl-cart-btn ${inCart ? 'in-cart' : ''}`}
                    onClick={(e) => { e.stopPropagation(); onCartToggle(); }}
                    title={inCart ? 'Remove from cart' : 'Add to application cart'}
                >
                    {inCart
                        ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" /></svg>
                    }
                    <span>{inCart ? 'Added' : 'Add to Cart'}</span>
                </button>
            </div>

            <div className="pl-card-body">
                <div className="pl-price">{priceDisplay}</div>

                {/* Unit summary rows for multi-unit properties */}
                {hasMultipleUnits ? (
                    <div className="pl-unit-rows">
                        {matchedUnits.slice(0, 4).map(u => (
                            <div key={u.id} className="pl-unit-row">
                                <span className="pl-unit-row-num">#{u.unit_number}</span>
                                <span className="pl-unit-row-specs">
                                    {u.bedrooms === 0 ? 'Studio' : u.bedrooms != null ? `${u.bedrooms}bd` : ''}
                                    {u.bathrooms ? ` · ${u.bathrooms}ba` : ''}
                                    {u.square_footage ? ` · ${u.square_footage.toLocaleString()} sqft` : ''}
                                </span>
                                <span className="pl-unit-row-price">{formatPrice(u.monthly_rent)}/mo</span>
                            </div>
                        ))}
                        {matchedUnits.length > 4 && (
                            <div className="pl-unit-more">+{matchedUnits.length - 4} more units</div>
                        )}
                    </div>
                ) : (
                    // Single unit: show specs inline
                    <div className="pl-specs">
                        {matchedUnits[0]?.bedrooms != null && (
                            <span className="pl-spec">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                                </svg>
                                {matchedUnits[0].bedrooms === 0 ? 'Studio' : `${matchedUnits[0].bedrooms} bd`}
                            </span>
                        )}
                        {matchedUnits[0]?.bathrooms && (
                            <span className="pl-spec">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.683 3 4 3.683 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" /><line x1="2" y1="12" x2="22" y2="12" />
                                </svg>
                                {matchedUnits[0].bathrooms} ba
                            </span>
                        )}
                        {matchedUnits[0]?.square_footage && (
                            <span className="pl-spec">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
                                </svg>
                                {matchedUnits[0].square_footage.toLocaleString()} sqft
                            </span>
                        )}
                    </div>
                )}

                <div className="pl-meta">
                    <div className="pl-name">{property.property_name || 'Rental Property'}</div>
                    <div className="pl-address">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                        </svg>
                        {property.address || 'Address not available'}
                    </div>
                </div>

                <div className="pl-card-actions">
                    <button className="pl-btn-outline" onClick={(e) => { e.stopPropagation(); onClick(); }}>View Details</button>
                    <button className="pl-btn-primary" onClick={(e) => { e.stopPropagation(); onApply(e); }}>Apply Now</button>
                </div>
            </div>
        </div>
    );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const PropertyListing = () => {
    const [properties, setProperties]   = useState([]);
    const [filtered, setFiltered]       = useState([]);
    const [loading, setLoading]         = useState(true);
    const [geocoding, setGeocoding]     = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode]       = useState('grid');  // 'grid' | 'list' | 'map'
    const [sortBy, setSortBy]           = useState('default');
    const [activeMapProperty, setActiveMapProperty] = useState(null);
    const [cartOpen, setCartOpen]       = useState(false);
    const [cart, setCart]               = useState(() => {
        try { return JSON.parse(localStorage.getItem('rentafai_cart') || '[]'); } catch { return []; }
    });
    const [filters, setFilters] = useState({
        minPrice: '', maxPrice: '', minBeds: '', minBaths: '', propertyType: ''
    });
    const navigate = useNavigate();

    useEffect(() => { localStorage.setItem('rentafai_cart', JSON.stringify(cart)); }, [cart]);
    useEffect(() => { fetchProperties(); }, []);
    useEffect(() => { applyFilters(); }, [properties, searchQuery, filters, sortBy]);

    const fetchProperties = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_BASE_URL}/api/properties`);
            if (!res.ok) return;
            const data = await res.json();

            const enriched = await Promise.all(data.map(async (p) => {
                try {
                    const ur = await fetch(`${API_BASE_URL}/api/properties/${p.id}/units`);
                    if (ur.ok) {
                        const units = await ur.json();
                        const avail = units.filter(u => u.status === 'vacant' && u.monthly_rent != null);
                                return {
                            ...p, allUnits: units, availableUnits: avail,
                            minRent: avail.length ? Math.min(...avail.map(u => u.monthly_rent)) : null,
                            maxRent: avail.length ? Math.max(...avail.map(u => u.monthly_rent)) : null,
                            bedroomOptions: [...new Set(avail.map(u => u.bedrooms).filter(b => b != null))].sort((a, b) => a - b)
                        };
                    }
                } catch {}
                return { ...p, allUnits: [], availableUnits: [], minRent: null, maxRent: null, bedroomOptions: [] };
            }));
            setProperties(enriched);

            // Geocode all properties for map view (run in background)
            geocodeAll(enriched);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const geocodeAll = async (props) => {
        setGeocoding(true);
        const updated = await Promise.all(props.map(async (p) => {
            if (p.latitude && p.longitude) return p;
            const coords = await geocodeAddress(p.address);
            return coords ? { ...p, latitude: coords.lat, longitude: coords.lng } : p;
        }));
        setProperties(updated);
        setGeocoding(false);
    };

    const applyFilters = useCallback(() => {
        let list = [...properties];

        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            list = list.filter(p =>
                p.property_name?.toLowerCase().includes(q) ||
                p.address?.toLowerCase().includes(q) ||
                p.city?.toLowerCase().includes(q)
            );
        }

        // Filter available units per property based on beds/baths
        list = list.map(p => {
            let matchedUnits = p.availableUnits || [];
            if (filters.minBeds !== '') {
                matchedUnits = matchedUnits.filter(u => u.bedrooms != null && u.bedrooms >= parseInt(filters.minBeds));
            }
            if (filters.minBaths !== '') {
                matchedUnits = matchedUnits.filter(u => u.bathrooms != null && u.bathrooms >= parseFloat(filters.minBaths));
            }
            // Recalculate price range from matched units
            const prices = matchedUnits.map(u => u.monthly_rent).filter(Boolean);
            return {
                ...p,
                matchedUnits,
                minRent: prices.length ? Math.min(...prices) : p.minRent,
                maxRent: prices.length ? Math.max(...prices) : p.maxRent,
            };
        });

        // Hide properties with no matched units when bed/bath filter is active
        if (filters.minBeds !== '' || filters.minBaths !== '') {
            list = list.filter(p => p.matchedUnits.length > 0);
        }

        if (filters.minPrice) list = list.filter(p => p.minRent && p.minRent >= +filters.minPrice);
        if (filters.maxPrice) list = list.filter(p => p.minRent && p.minRent <= +filters.maxPrice);
        if (filters.propertyType) list = list.filter(p => p.property_type?.toLowerCase() === filters.propertyType.toLowerCase());

        if (sortBy === 'price-asc')  list.sort((a, b) => (a.minRent || Infinity) - (b.minRent || Infinity));
        if (sortBy === 'price-desc') list.sort((a, b) => (b.maxRent || 0) - (a.maxRent || 0));
        if (sortBy === 'avail')      list.sort((a, b) => (b.matchedUnits?.length || b.availableUnits?.length || 0) - (a.matchedUnits?.length || a.availableUnits?.length || 0));

        setFiltered(list);
    }, [properties, searchQuery, filters, sortBy]);

    const toggleCart   = useCallback((p) => setCart(prev => prev.some(x => x.id === p.id) ? prev.filter(x => x.id !== p.id) : [...prev, p]), []);
    const removeCart   = useCallback((id) => setCart(prev => prev.filter(p => p.id !== id)), []);
    const clearCart    = useCallback(() => setCart([]), []);
    const isInCart     = useCallback((id) => cart.some(p => p.id === id), [cart]);
    const hasFilter    = filters.minPrice || filters.maxPrice || filters.minBeds || filters.minBaths || filters.propertyType;

    const clearFilters = () => {
        setFilters({ minPrice: '', maxPrice: '', minBeds: '', minBaths: '', propertyType: '' });
        setSearchQuery('');
    };

    const priceLabel = filters.minPrice && filters.maxPrice
        ? `$${(+filters.minPrice).toLocaleString()} – $${(+filters.maxPrice).toLocaleString()}`
        : filters.minPrice ? `$${(+filters.minPrice).toLocaleString()}+`
        : filters.maxPrice ? `Up to $${(+filters.maxPrice).toLocaleString()}`
        : 'Price';
    const bedsLabel  = filters.minBeds  !== '' ? (+filters.minBeds === 0 ? 'Studio+' : `${filters.minBeds}+ Beds`) : 'Beds';
    const bathsLabel = filters.minBaths !== '' ? `${filters.minBaths}+ Baths` : 'Baths';
    const typeLabel  = filters.propertyType || 'Home Type';

    const mapProperties = filtered.filter(p => p.latitude && p.longitude);

    if (loading) {
        return (
            <div className="pl-page">
                <div className="pl-loading">
                    <div className="pl-spinner" />
                    <p>Finding available properties…</p>
                </div>
            </div>
        );
    }

    return (
        <div className="pl-page">

            {/* ── Navbar ─────────────────────────────────────── */}
            <nav className="pl-nav">
                <div className="pl-nav-inner">
                    <div className="pl-nav-brand">
                        <img src={icon} alt="RentafAI" className="pl-logo" />
                    </div>
                    <div className="pl-search-bar">
                        <svg className="pl-search-icon" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            type="text"
                            className="pl-search-input"
                            placeholder="City, neighborhood, address…"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && <button className="pl-search-clear" onClick={() => setSearchQuery('')}>×</button>}
                    </div>
                    <button
                        className={`pl-cart-nav-btn ${cart.length > 0 ? 'has-items' : ''}`}
                        onClick={() => setCartOpen(true)}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                        </svg>
                        {cart.length > 0 && <span className="pl-cart-badge">{cart.length}</span>}
                        <span className="pl-cart-label">Cart</span>
                    </button>
                </div>
            </nav>

            {/* ── Filter Bar ─────────────────────────────────── */}
            <div className="pl-filterbar">
                <div className="pl-filterbar-inner">
                    <div className="pl-filters-left">
                        {/* Price */}
                        <FilterDropdown label={priceLabel} active={!!(filters.minPrice || filters.maxPrice)}>
                            <div className="fdd-title">Monthly Rent</div>
                            <div className="fdd-row">
                                <div className="fdd-field">
                                    <label>Min</label>
                                    <select value={filters.minPrice} onChange={e => setFilters(f => ({ ...f, minPrice: e.target.value }))}>
                                        <option value="">No min</option>
                                        {[500,750,1000,1250,1500,1750,2000,2500,3000,4000,5000].map(v => <option key={v} value={v}>${v.toLocaleString()}</option>)}
                                    </select>
                                </div>
                                <span className="fdd-dash">–</span>
                                <div className="fdd-field">
                                    <label>Max</label>
                                    <select value={filters.maxPrice} onChange={e => setFilters(f => ({ ...f, maxPrice: e.target.value }))}>
                                        <option value="">No max</option>
                                        {[750,1000,1250,1500,1750,2000,2500,3000,4000,5000,7500,10000].map(v => <option key={v} value={v}>${v.toLocaleString()}</option>)}
                                    </select>
                                </div>
                            </div>
                        </FilterDropdown>

                        {/* Beds */}
                        <FilterDropdown label={bedsLabel} active={filters.minBeds !== ''}>
                            <div className="fdd-title">Bedrooms</div>
                            <div className="fdd-chip-row">
                                {[['', 'Any'], ['0', 'Studio'], ['1', '1+'], ['2', '2+'], ['3', '3+'], ['4', '4+']].map(([val, lbl]) => (
                                    <button key={val} className={`fdd-chip ${filters.minBeds === val ? 'active' : ''}`} onClick={() => setFilters(f => ({ ...f, minBeds: val }))}>{lbl}</button>
                                ))}
                            </div>
                        </FilterDropdown>

                        {/* Baths */}
                        <FilterDropdown label={bathsLabel} active={filters.minBaths !== ''}>
                            <div className="fdd-title">Bathrooms</div>
                            <div className="fdd-chip-row">
                                {[['', 'Any'], ['1', '1+'], ['1.5', '1.5+'], ['2', '2+'], ['3', '3+']].map(([val, lbl]) => (
                                    <button key={val} className={`fdd-chip ${filters.minBaths === val ? 'active' : ''}`} onClick={() => setFilters(f => ({ ...f, minBaths: val }))}>{lbl}</button>
                                ))}
                            </div>
                        </FilterDropdown>

                        {/* Home Type */}
                        <FilterDropdown label={typeLabel} active={!!filters.propertyType}>
                            <div className="fdd-title">Home Type</div>
                            <div className="fdd-chip-row">
                                {['', 'Apartment', 'House', 'Condo', 'Townhouse'].map(t => (
                                    <button key={t} className={`fdd-chip ${filters.propertyType === t ? 'active' : ''}`} onClick={() => setFilters(f => ({ ...f, propertyType: t }))}>{t || 'All'}</button>
                                ))}
                        </div>
                        </FilterDropdown>

                        {hasFilter && <button className="pl-clear-all" onClick={clearFilters}>× Clear</button>}
                    </div>

                    <div className="pl-filters-right">
                        <select className="pl-sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                            <option value="default">Sort: Recommended</option>
                            <option value="price-asc">Price: Low → High</option>
                            <option value="price-desc">Price: High → Low</option>
                            <option value="avail">Most Units Available</option>
                        </select>

                        {/* View toggle: Grid / List / Map */}
                        <div className="pl-view-toggle">
                            <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')} title="Grid">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
                                    <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
                                </svg>
                            </button>
                            <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')} title="List">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <rect x="3" y="4" width="18" height="3" rx="1" /><rect x="3" y="10.5" width="18" height="3" rx="1" /><rect x="3" y="17" width="18" height="3" rx="1" />
                                </svg>
                            </button>
                            <button className={viewMode === 'map' ? 'active' : ''} onClick={() => setViewMode('map')} title="Map">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
                    </div>

            {/* ── Map View ───────────────────────────────────── */}
            {viewMode === 'map' && (
                <div className="pl-map-layout">
                    {/* Map */}
                    <div className="pl-map-panel">
                        {geocoding && <div className="pl-map-geocoding">Locating properties…</div>}
                        <MapContainer
                            center={[39.5, -98.35]}
                            zoom={4}
                            style={{ height: '100%', width: '100%' }}
                            scrollWheelZoom={true}
                        >
                            <TileLayer
                                attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <FitBounds properties={filtered} />
                            {filtered.filter(p => p.latitude && p.longitude).map(p => (
                                <Marker
                                    key={p.id}
                                    position={[p.latitude, p.longitude]}
                                    icon={activeMapProperty?.id === p.id ? activeIcon : blueIcon}
                                    eventHandlers={{ click: () => setActiveMapProperty(p) }}
                                >
                                    <Popup>
                                        <div className="map-popup">
                                            <div className="map-popup-price">
                                                {p.minRent ? `${formatPrice(p.minRent)}/mo` : 'Contact'}
                                            </div>
                                            <div className="map-popup-name">{p.property_name}</div>
                                            <div className="map-popup-addr">{p.address}</div>
                                            {(p.matchedUnits || p.availableUnits || []).length > 0 && (
                                                <div className="map-popup-units">
                                                    {(p.matchedUnits || p.availableUnits).length} unit(s) available
                                                </div>
                                            )}
                                            <button className="map-popup-btn" onClick={() => navigate(`/listings/${p.id}`)}>
                                                View Details
                                            </button>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                        {mapProperties.length === 0 && !geocoding && (
                            <div className="pl-map-no-coords">
                                <p>No properties have coordinates yet. Addresses are being geocoded automatically.</p>
                            </div>
                        )}
                    </div>

                    {/* Map sidebar: property list */}
                    <div className="pl-map-sidebar">
                        <div className="pl-map-sidebar-header">
                            <span>{filtered.length} rental{filtered.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="pl-map-sidebar-list">
                            {filtered.map(p => (
                                <div
                                    key={p.id}
                                    className={`pl-map-sidebar-item ${activeMapProperty?.id === p.id ? 'active' : ''}`}
                                    onClick={() => setActiveMapProperty(p)}
                                >
                                    <div className="pl-map-si-image">
                                        <svg viewBox="0 0 80 60" fill="none">
                                            <rect width="80" height="60" fill="#dde3f0" />
                                            <path d="M20 42V28l20-14 20 14v14" stroke="#8fa3cc" strokeWidth="2.5" fill="none" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <div className="pl-map-si-info">
                                        <div className="pl-map-si-price">
                                            {p.minRent ? `${formatPrice(p.minRent)}/mo` : 'Contact'}
                                        </div>
                                        <div className="pl-map-si-name">{p.property_name}</div>
                                        <div className="pl-map-si-addr">{p.address}</div>
                                        {(p.matchedUnits || p.availableUnits || []).length > 0 && (
                                            <div className="pl-map-si-units">
                                                {(p.matchedUnits || p.availableUnits).length} unit(s) avail
                                            </div>
                                        )}
                </div>
                            <button
                                        className="pl-map-si-btn"
                                        onClick={(e) => { e.stopPropagation(); navigate(`/listings/${p.id}`); }}
                                    >→</button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Grid / List View ────────────────────────────── */}
            {viewMode !== 'map' && (
                <main className="pl-main">
                    <div className="pl-results-header">
                        <div>
                            <h1 className="pl-results-title">
                                {filtered.length.toLocaleString()} rental{filtered.length !== 1 ? 's' : ''} available
                            </h1>
                            <p className="pl-results-sub">
                                {searchQuery ? `Results for "${searchQuery}"` : 'All locations'}
                                {(filters.minBeds !== '' || filters.minBaths !== '') && ' · filtered by unit specs'}
                            </p>
                        </div>
                        {cart.length > 0 && (
                            <button className="pl-cart-cta" onClick={() => setCartOpen(true)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                                </svg>
                                {cart.length} in cart — Apply to All
                            </button>
                        )}
                    </div>

                    {filtered.length === 0 ? (
                        <div className="pl-empty">
                            <div className="pl-empty-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                            </div>
                            <h2>No properties found</h2>
                            <p>Try adjusting your filters or searching a different location.</p>
                            <button className="pl-btn-primary standalone" onClick={clearFilters}>Clear All Filters</button>
                        </div>
                    ) : (
                        <div className={`pl-grid ${viewMode}`}>
                            {filtered.map(p => (
                                <PropertyCard
                                    key={p.id}
                                    property={p}
                                    viewMode={viewMode}
                                    inCart={isInCart(p.id)}
                                    onCartToggle={() => toggleCart(p)}
                                    onClick={() => navigate(`/listings/${p.id}`)}
                                    onApply={(e) => { e.stopPropagation(); navigate(`/apply?propertyId=${p.id}`); }}
                                />
                            ))}
                        </div>
                    )}
                </main>
            )}

            {/* Cart drawer */}
            {cartOpen && (
                <CartDrawer
                    cart={cart}
                    onRemove={removeCart}
                    onClear={clearCart}
                    onClose={() => setCartOpen(false)}
                />
            )}

            {/* AI Rental Assistant */}
            <ListingsAssistant />
        </div>
    );
};

export default PropertyListing;
